import React, { useEffect, useState, useRef } from "react";
import TemplateProgressControls from "components/TemplateBuilderForm/TemplateProgressControls";
import DetailLoader from "components/DetailLoader";
import { withRouter } from "next/router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { hasAnyUpdates } from "utils/data";
import {
  getResourceAppMerchantData,
  getCtxModelsForResourceAndUUID
} from "utils/mapStateToProps";
import {
  fetchFeatureGroupFeatures,
  bulkUpdateFeatureGroups
} from "actions/featureGroup";
import { bulkUpdateFeatureGroupsParams } from "utils/feature";
import TemplateFeatureTablePreview from "./TemplateFeatureTablePreview";
import { useFormikContext } from "formik";
import { MODEL_RESOURCES } from "utils/constants/models";
import { useTemplateContinue } from "utils/formContext";
import {
  getInitialRecurringProductPricesLayout,
  adjustLayoutForFreeProducts,
  getOrderedProducts
} from "components/FormerEditor/common";
import {
  transformNewProductsPrices,
  transformProductsPrices
} from "utils/product";
import { PAGE_BUILDER_FEATURE_GROUPS_ONBOARDING } from "constants/education";
import UserEducationModal from "components/UserEducationModal";

const TemplateFeaturesPicker = ({
  tracking,
  features,
  featureGroups,
  resource,
  resourceID,
  resourceUUID,
  actions
}) => {
  const featureFormRef = useRef(null);
  const { values, setValues } = useFormikContext();
  const [fetchingFeatureGroups, setFetchingFeatureGroups] = useState(false);

  const { nextAction } = useTemplateContinue();

  const hasNewProducts = Boolean(
    values.products && values.products.find(({ uuid }) => uuid)
  );

  useEffect(() => {
    const shouldFetchProductsPrices =
      resourceID &&
      resourceUUID &&
      resource &&
      !fetchingFeatureGroups &&
      !featureGroups.length;

    if (shouldFetchProductsPrices) {
      setFetchingFeatureGroups(true);
      actions.fetchFeatureGroupFeatures(
        {
          resourceID,
          resourceUUID,
          resource
        },
        () => {
          setFetchingFeatureGroups(false);
        }
      );
    }
  }, []);

  const transformedProducts = hasNewProducts
    ? transformNewProductsPrices(values.products)
    : transformProductsPrices(values.products);
  const orderedProducts = getOrderedProducts(transformedProducts);
  const initialLayout = getInitialRecurringProductPricesLayout(orderedProducts);
  const productLayout = adjustLayoutForFreeProducts(
    transformedProducts,
    initialLayout
  );

  const onSubmit = () => {
    const featureForm = featureFormRef.current;

    const data = bulkUpdateFeatureGroupsParams(
      featureForm.initialValues,
      featureForm.values
    );

    if (hasAnyUpdates(data)) {
      /**
       * If has new products then we dont submit the features form now
       * - We extend the parent form state with the values and send them along in the manfiest creation request
       */
      if (hasNewProducts) {
        const updatedValues = {
          ...values,
          ...data
        };

        setValues(updatedValues);
        nextAction(() => {
          featureForm.setSubmitting(false);
          featureForm.setTouched({});
        });
      } else {
        actions.bulkUpdateFeatureGroups(
          {
            resourceID,
            resourceUUID,
            resource,
            data
          },
          (err) => {
            featureForm.setSubmitting(false);
            if (err) {
              featureForm.setErrors(err);
            } else {
              featureForm.setTouched({});
              nextAction();
            }
          }
        );
      }
    } else {
      featureForm.setSubmitting(false);
      featureForm.setTouched({});
      nextAction();
      console.warn("FeatureTablePreviewContainer: No updates to be made");
    }
  };

  return (
    <div className="flex flex-column w-100 relative">
      <div
        style={{ top: 56 }}
        className="sticky top-0 pb3 bg-white bb hairline-1 z-6"
      >
        <TemplateProgressControls onNext={onSubmit} />
      </div>
      <div className="flex flex-column pt4 pl3">
        <div className="f3 f2-ns fw5">Add your features</div>
        <div className="f4 f3-ns pt3 lh-copy">
          Add and group each of your product features to help your customers
          compare their options.
        </div>
      </div>
      {fetchingFeatureGroups ? (
        <DetailLoader />
      ) : (
        <div className="pt3">
          <TemplateFeatureTablePreview
            innerRef={featureFormRef}
            onSubmit={onSubmit}
            features={features}
            tracking={tracking}
            products={transformedProducts}
            featureGroups={featureGroups}
            productLayout={productLayout}
            resourceID={resourceID}
            resource={resource}
            resourceUUID={resourceUUID}
          />
          <UserEducationModal
            keys={[PAGE_BUILDER_FEATURE_GROUPS_ONBOARDING]}
            innerRef={featureFormRef}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (
  state,
  { router, application, resource, resourceUUID }
) => {
  const ctxResourceData = getResourceAppMerchantData(state, {
    router,
    application,
    resource,
    resourceUUID
  });
  const features = getCtxModelsForResourceAndUUID(
    state,
    MODEL_RESOURCES.FEATURE,
    ctxResourceData.resource
  );
  const featureGroups = getCtxModelsForResourceAndUUID(
    state,
    MODEL_RESOURCES.FEATURE_GROUP,
    ctxResourceData.resource
  );

  const base = {
    features,
    featureGroups,
    ...ctxResourceData
  };

  return base;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchFeatureGroupFeatures,
      bulkUpdateFeatureGroups
    },
    dispatch
  )
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TemplateFeaturesPicker)
);
